import { Link } from "react-router-dom"
import type { NostrEvent } from "@/nostr/types"
import { threadParentId } from "@/nostr/api/thread"
import { useTranslation } from "@/i18n"
import { noteIdOf } from "@/utils/nip19"
import { NoteCard } from "./NoteCard"

export interface ThreadRepliesProps {
  childrenMap: Map<string, NostrEvent[]>
  /** The event every direct reply in this branch answers. */
  parentEvent: NostrEvent
  depth?: number
  maxDepth?: number
  /** Author of the focused note, shown as "OP" on their replies. */
  opPubkey?: string
  onReplied?: () => void
}

/**
 * Recursive reply tree.
 *
 * Every card knows its parent event, so it renders an explicit
 * "Replying to @name" line, and each level is indented with a connector rail and
 * elbow pointing at the card it answers.
 */
export function ThreadReplies({
  childrenMap,
  parentEvent,
  depth = 0,
  maxDepth = 4,
  opPubkey,
  onReplied,
}: ThreadRepliesProps) {
  const { t } = useTranslation()
  const replies = childrenMap.get(parentEvent.id) ?? []
  if (replies.length === 0) return null

  return (
    <div className="thread-children" data-depth={depth}>
      {replies.map((reply) => {
        const nested = childrenMap.get(reply.id) ?? []
        return (
          <div key={reply.id} className="thread-branch">
            <NoteCard
              event={reply}
              opPubkey={opPubkey}
              replyTo={{ event: parentEvent }}
              onReplied={onReplied}
            />
            {nested.length > 0 &&
              (depth < maxDepth ? (
                <ThreadReplies
                  childrenMap={childrenMap}
                  parentEvent={reply}
                  depth={depth + 1}
                  maxDepth={maxDepth}
                  opPubkey={opPubkey}
                  onReplied={onReplied}
                />
              ) : (
                <Link to={`/n/${noteIdOf(reply.id)}`} className="thread-continue">
                  {nested.length} {t("more replies in this thread")} →
                </Link>
              ))}
          </div>
        )
      })}
    </div>
  )
}

/**
 * Replies whose parent is not available on the connected relays. They are shown
 * separately with the missing parent id instead of pretending they are direct
 * replies of the focused note.
 */
export function OrphanReplies({
  replies,
  onReplied,
}: {
  replies: NostrEvent[]
  onReplied?: () => void
}) {
  const { t } = useTranslation()
  if (replies.length === 0) return null

  return (
    <div className="thread-orphans">
      <p className="section-label">{t("Replies to unavailable notes")}</p>
      {replies.map((reply) => (
        <NoteCard
          key={reply.id}
          event={reply}
          replyTo={{ missingId: threadParentId(reply) }}
          onReplied={onReplied}
        />
      ))}
    </div>
  )
}
